import React, { useState, useRef, useEffect, useCallback } from 'react';
import QuoteFormNav from './QuoteFormNav';
import { useNavigate } from 'react-router-dom';
import QuoteFormLayout from './QuoteFormLayout';
import DescriptionButton from './DescriptionButton';
import { Trash2 } from 'lucide-react';
import { Button } from './components/ui/button';
import { Input } from './components/ui/input';
import { useQuote } from './QuoteContext';
import './QuoteForm.css';
import CatalogueModal from './CatalogueModal';
import DimensionnementModal from './DimensionnementModal';
import DimensionnementClimModal from './DimensionnementClimModal';
import DimensionnementPompeModal from './DimensionnementPompeModal';
import ATPModal from './ATPModal';
import DimensionnementChauffeEauModal from './DimensionnementChauffeEauModal';
import DimensionnementGenerateurModal from './DimensionnementGenerateurModal';
import DimensionnementFrigoModal from './DimensionnementFrigoModal';
import { AnimatePresence } from 'framer-motion';

const QuoteForm = () => {
  const navigate = useNavigate();
  const { updateQuoteData, quoteData } = useQuote();

  const getInitialData = () => {
    if (quoteData.items && quoteData.items.length > 0) {
      return {
        items: quoteData.items,
        deliveryCost: quoteData.deliveryCost || 0,
        conditionsValidite: quoteData.conditionsValidite || '',
        conditionsPaiement: quoteData.conditionsPaiement || '',
        conditionsGaranties: quoteData.conditionsGaranties || '',
        vatOption: quoteData.vatOption || 'without_vat',
        discount: quoteData.discount || 0,
        discountPct: quoteData.discountPct || 0,
      };
    }
    const savedData = localStorage.getItem('solarquotepro_quote');
    if (savedData) {
      try { return JSON.parse(savedData); } catch (_) {}
    }
    return { items: [], deliveryCost: 0, vatOption: 'without_vat', discount: 0, conditionsValidite: '', conditionsPaiement: '', conditionsGaranties: '' };
  };

  const initial = getInitialData();
  const isResettingRef = useRef(false);
  const [items, setItems] = useState(initial.items);
  const [deliveryCost, setDeliveryCost] = useState(initial.deliveryCost);
  const [vatOption, setVatOption] = useState(initial.vatOption);
  const [discount, setDiscount] = useState(initial.discount);
  const [discountPct, setDiscountPct] = useState(initial.discountPct || 0);
  const [showEuros, setShowEuros] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [showConditions, setShowConditions] = useState(false);
  const [conditionsValidite, setConditionsValidite] = useState(() => {
    try {
      const stored = JSON.parse(localStorage.getItem('solarquotepro_quote') || '{}');
      return stored.conditionsValidite || '';
    } catch(e) { return ''; }
  });
  const [conditionsPaiement, setConditionsPaiement] = useState(() => {
    try {
      const stored = JSON.parse(localStorage.getItem('solarquotepro_quote') || '{}');
      return stored.conditionsPaiement || '';
    } catch(e) { return ''; }
  });
  const [conditionsGaranties, setConditionsGaranties] = useState(() => {
    try {
      const stored = JSON.parse(localStorage.getItem('solarquotepro_quote') || '{}');
      return stored.conditionsGaranties || '';
    } catch(e) { return ''; }
  });
  const [subtotal, setSubtotal] = useState(0);
  const [vat, setVat] = useState(0);
  const [total, setTotal] = useState(0);
  const [showCatalogue, setShowCatalogue] = useState(false);
  const [showDimensionnement, setShowDimensionnement] = useState(false);
  const [showDimensionnementClim, setShowDimensionnementClim] = useState(false);
  const [showDimensionnementPompe, setShowDimensionnementPompe] = useState(false);
  const [showATP, setShowATP] = useState(false);
  const [showChauffeEau, setShowChauffeEau] = useState(false);
  const [showFrigo, setShowFrigo] = useState(false);
  const [showGenerateur, setShowGenerateur] = useState(false);

  const calculateTotals = useCallback(() => {
    const itemsTotal = items.reduce((sum, item) =>
      sum + (item.quantity || 0) * (item.unitPrice || 0), 0);
    const calculatedDeliveryCost = parseFloat(deliveryCost) || 0;
    const baseSubtotal = itemsTotal + calculatedDeliveryCost - (parseFloat(discount) || 0);
    const calculatedSubtotal = Math.max(0, baseSubtotal);
    const calculatedVat = vatOption === 'with_vat' ? calculatedSubtotal * 0.18 : 0;
    const calculatedTotal = calculatedSubtotal + calculatedVat;
    setSubtotal(calculatedSubtotal);
    setVat(calculatedVat);
    setTotal(calculatedTotal);
    return { calculatedSubtotal, calculatedDeliveryCost, calculatedVat, calculatedTotal };
  }, [items, deliveryCost, vatOption, discount, discountPct]);

  useEffect(() => {
    const { calculatedSubtotal, calculatedDeliveryCost, calculatedVat, calculatedTotal } = calculateTotals();
    if (isResettingRef.current) return;
      try {
        const currentStored = JSON.parse(localStorage.getItem('solarquotepro_quote') || '{}');
        localStorage.setItem('solarquotepro_quote', JSON.stringify({
          items, 
          deliveryCost, 
          vatOption, 
          discount,
          discountPct, 
          conditionsValidite: conditionsValidite || currentStored.conditionsValidite || '', 
          conditionsPaiement: conditionsPaiement || currentStored.conditionsPaiement || '', 
          conditionsGaranties: conditionsGaranties || currentStored.conditionsGaranties || '',
          termsConditions: currentStored.termsConditions || ''
        }));
      } catch(e) {}
    updateQuoteData({
      items, vatOption,
      discount: parseFloat(discount) || 0,
      discountPct: parseFloat(discountPct) || 0,
      subtotal: calculatedSubtotal,
      deliveryCost: calculatedDeliveryCost,
      vatRate: 18,
      vat: calculatedVat,
      totalAmount: calculatedSubtotal,
      totalWithVat: calculatedTotal,
      conditionsValidite,
      conditionsPaiement,
      conditionsGaranties,
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, deliveryCost, vatOption, discount, discountPct, conditionsValidite, conditionsPaiement, conditionsGaranties]);


  const moveItem = (itemId, direction) => {
    setItems(prev => {
      const arr = Array.from(prev);
      const fromIndex = arr.findIndex(it => String(it.id) === String(itemId));
      const toIndex = fromIndex + direction;
      if (toIndex < 0 || toIndex >= arr.length) return prev;
      const [removed] = arr.splice(fromIndex, 1);
      arr.splice(toIndex, 0, removed);
      return arr;
    });
  };
  const validateBeforeNext = () => {
    const errors = [];
    items.filter(item => !item._titre).forEach((item, idx) => {
      if (!item.description || !item.description.trim()) {
        errors.push(`❌ Article ${idx + 1} : description manquante`);
      }
      if (!item.quantity || item.quantity <= 0) {
        errors.push(`❌ Article ${idx + 1} : quantité invalide`);
      }
      if (!item._offert && (item.unitPrice === undefined || item.unitPrice === null || item.unitPrice < 0)) {
        errors.push(`❌ Article ${idx + 1} : prix unitaire invalide`);
      }
    });
    const itemsTotal = items.filter(i => !i._titre && !i._offert).reduce((s, i) => s + (i.quantity||0)*(i.unitPrice||0), 0);
    const hasOnlyOfferts = items.filter(i => !i._titre).every(i => i._offert);
      if (items.length > 0 && itemsTotal === 0 && !hasOnlyOfferts) errors.push('❌ Le montant total est zéro — vérifiez les prix');
      if (typeof setValidationErrors === 'function') { setValidationErrors(errors); }
      return errors.length === 0;
   };
   const goToPreviousStep = () => navigate('/company-info');
  const goToNextStep = () => { if (validateBeforeNext()) navigate('/confirmation'); };

  const addItem = () => {
    setValidationErrors([]);
    setItems([...items, { id: Date.now(), image: '', description: '', quantity: 1, unitPrice: 0 }]);
  };

  const removeItem = (id) => setItems(items.filter(item => item.id !== id));

  const updateItem = (id, field, value) => {
    setItems(items.map(item => item.id === id ? { ...item, [field]: value } : item));
  };

 const handleAddFromCatalogue = (item) => {
      setItems(prev => [...prev, {
        id: Date.now() + Math.random(),
        description: item.description,
        quantity: item.quantity,
        unitPrice: item.unitPrice,
        image: item.image || null,
        _titre: item._titre || false,
        categorie: item.categorie || '',
        _offert: item._offert || false,
      }]);
   };
   const handleImageUpload = (id, event) => {
    const file = event.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => {
      const img = new Image();
      img.onload = () => {
        const MAX = 300;
        const scale = Math.min(1, MAX / Math.max(img.width, img.height));
        const canvas = document.createElement('canvas');
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        const compressed = canvas.toDataURL('image/jpeg', 0.70);
        updateItem(id, 'image', compressed);
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  };

  const handleSave = () => {
    if (isResettingRef.current) return;
    localStorage.setItem('solarquotepro_quote', JSON.stringify({ items, deliveryCost, vatOption, discount, discountPct }));
    setSaveSuccess(true);
    setTimeout(() => setSaveSuccess(false), 3000);
  };

  const handleReset = () => {
    if (window.confirm('\u00cates-vous s\u00fbr de vouloir r\u00e9initialiser le formulaire ?')) {
      isResettingRef.current = true;
      localStorage.removeItem('solarquotepro_quote');
      setItems([]);
      setDeliveryCost(0);
      setVatOption('without_vat');
      setDiscount(0);
      setDiscountPct(0);
      setConditionsValidite('');
      setConditionsPaiement('');
      setConditionsGaranties('');
      setValidationErrors([]);
      setTimeout(() => { isResettingRef.current = false; }, 50);
    }
  };

  const formatFCFA = (amount) => new Intl.NumberFormat('fr-FR').format(Math.round(amount || 0));

  return (
    <QuoteFormLayout>
      {validationErrors.length > 0 && (
        <div className="mb-4 p-4 bg-red-50 border border-red-300 rounded-lg">
          <h4 className="font-semibold text-red-700 mb-2">Impossible de continuer :</h4>
          <ul className="space-y-1">
            {validationErrors.map((err, i) => (
              <li key={i} className="text-red-600 text-sm">{err}</li>
            ))}
          </ul>
        </div>
      )}

      {saveSuccess && (
        <div className="mb-4 p-3 bg-green-50 border border-green-300 rounded text-green-700 text-sm">
          ✓ Données sauvegardées avec succès !
        </div>
      )}

      <div id="section-articles" className="overflow-x-auto">
        <table className="min-w-full bg-white shadow-md rounded-lg overflow-hidden">
          <thead className="bg-gray-200">
            <tr>
              <th className="py-2 px-4 text-left" style={{width:'32px'}}></th><th className="py-2 px-4 text-left">N°</th>
              <th className="py-2 px-4 text-left">Image</th>
              <th className="py-2 px-4 text-left">Description</th>
              <th className="py-2 px-4 text-left">Quantité</th>
              <th className="py-2 px-4 text-left">Prix unitaire (FCFA)</th>
              <th className="py-2 px-4 text-left">Prix total (FCFA)</th>
              <th className="py-2 px-4 text-left">Actions</th>
            </tr>
          </thead>
          <tbody>
{items.filter(item => !item._titre).map((item, index) => (
              <tr key={item.id} className="border-b hover:bg-gray-50">
                <td className="py-2 px-4" style={{textAlign:'center', whiteSpace:'nowrap'}}>
  <div style={{display:'flex', flexDirection:'column', gap:'1px'}}>
    <button type="button" data-id={String(item.id)} onClick={(e) => { moveItem(e.currentTarget.dataset.id, -1); }}
      style={{background:'none', border:'none', cursor: index===0 ? 'default':'pointer', color: index===0 ? '#d1d5db':'#6b7280', padding:'1px 4px', fontSize:'12px', lineHeight:1}}>▲</button>
    <button type="button" data-id={String(item.id)} onClick={(e) => { moveItem(e.currentTarget.dataset.id, 1); }}
      style={{background:'none', border:'none', cursor: index===items.length-1 ? 'default':'pointer', color: index===items.length-1 ? '#d1d5db':'#6b7280', padding:'1px 4px', fontSize:'12px', lineHeight:1}}>▼</button>
  </div>
</td>
                <td className="py-2 px-4">{index + 1}</td>
                <td className="py-2 px-4">
                  {item.image ? (
                    <div className="relative">
                      <img src={item.image} alt="Article" className="w-16 h-16 object-contain rounded border border-gray-200" />
                      <button
                        onClick={() => updateItem(item.id, 'image', '')}
                        className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-4 h-4 text-xs flex items-center justify-center"
                      >×</button>
                    </div>
                  ) : (
                    <div className="relative group">
                      <Input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleImageUpload(item.id, e)}
                        className="text-sm"
                      />
                      <div className="absolute bottom-full left-0 mb-2 hidden group-hover:block z-10 w-60 bg-gray-900 text-white text-xs rounded-lg px-3 py-2 shadow-xl pointer-events-none">
                        <p className="font-semibold text-yellow-300 mb-1">💡 Conseil image</p>
                        <p>Pour un rendu PDF optimal, utilisez des images <span className="text-yellow-300 font-semibold">PNG avec fond transparent</span>.</p>
                        <p className="mt-1 text-gray-300">Les JPG avec fond blanc peuvent contraster avec le fond du tableau.</p>
                        <div className="absolute top-full left-4 border-4 border-transparent border-t-gray-900"></div>
                      </div>
                    </div>
                  )}
                </td>
                <td className="py-2 px-4">
                  <DescriptionButton
                    onDescriptionChange={(value) => updateItem(item.id, 'description', value)}
                    description={item.description}
                  />
                </td>
                <td className="py-2 px-4">
                  <Input
                    type="number"
                    min="1"
                    value={item.quantity === 0 ? '' : item.quantity}
                    onChange={(e) => updateItem(item.id, 'quantity', parseInt(e.target.value) || 1)}
                    className="w-20"
                  />
                </td>
                <td className="py-2 px-4">
                  {(item.categorie === 'Prestations' || item.categorie === 'prestations') ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      <Input
                        type="number"
                        min="0"
                        value={item._offert ? '' : (item.unitPrice === 0 ? '' : item.unitPrice)}
                        disabled={item._offert}
                        onChange={(e) => updateItem(item.id, 'unitPrice', parseFloat(e.target.value) || 0)}
                        className="w-32"
                        style={item._offert ? { background: '#f3f4f6', color: '#9ca3af' } : {}}
                      />
                      <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px', color: '#6b7280', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={!!item._offert}
                          onChange={(e) => updateItem(item.id, '_offert', e.target.checked)}
                          style={{ cursor: 'pointer', accentColor: '#059669' }}
                        />
                        Offert
                      </label>
                    </div>
                  ) : (
                    <Input
                      type="number"
                      min="0"
                      value={item.unitPrice === 0 ? '' : item.unitPrice}
                      onChange={(e) => updateItem(item.id, 'unitPrice', parseFloat(e.target.value) || 0)}
                      className="w-32"
                    />
                  )}
                </td>
                <td className="py-2 px-4 font-medium">
                  {formatFCFA((item.quantity || 0) * (item.unitPrice || 0))}
                </td>
                <td className="py-2 px-4">
<button type="button" onClick={() => removeItem(item.id)}
  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}>
  <Trash2 size={16} color="#ef4444" />
</button>
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr>
                <td colSpan="7" className="py-8 text-center text-gray-400">
                  Aucun article. Cliquez sur "Ajouter un article" ou "📦 Depuis le catalogue" pour commencer.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Boutons ajout article */}
      <div id="section-boutons">
      <div className="mt-4 flex gap-3 flex-wrap">
        <Button onClick={addItem} className="bg-green-500 text-white hover:bg-green-600">
          + Ajouter un article
        </Button>
        <button
          type="button"
          onClick={() => setShowCatalogue(true)}
          onMouseEnter={e => e.currentTarget.style.background='#1e3a8a'}
          onMouseLeave={e => e.currentTarget.style.background='#1e40af'}
          style={{
            background: '#1e40af', color: 'white', border: 'none',
            borderRadius: '8px', padding: '8px 20px', fontSize: '14px',
            fontWeight: 600, cursor: 'pointer', display: 'inline-flex',
            alignItems: 'center', gap: '6px', transition: 'background 0.15s',
          }}
        >
📦 Depuis le catalogue
        </button>
        <button
          type="button"
          onClick={() => setShowDimensionnement(true)}
          onMouseEnter={e => e.currentTarget.style.background='#065f46'}
          onMouseLeave={e => e.currentTarget.style.background='#047857'}
          style={{
            background: '#047857', color: 'white', border: 'none',
            borderRadius: '8px', padding: '8px 20px', fontSize: '14px',
            fontWeight: 600, cursor: 'pointer', display: 'inline-flex',
            alignItems: 'center', gap: '6px', transition: 'background 0.15s',
          }}
        >
          ⚡ Kit PV
        </button>
        <button
          type="button"
          onClick={() => setShowDimensionnementClim(true)}
          onMouseEnter={e => e.currentTarget.style.background='#0369a1'}
          onMouseLeave={e => e.currentTarget.style.background='#0284c7'}
          style={{
            background: '#0284c7', color: 'white', border: 'none',
            borderRadius: '8px', padding: '8px 20px', fontSize: '14px',
            fontWeight: 600, cursor: 'pointer', display: 'inline-flex',
            alignItems: 'center', gap: '6px', transition: 'background 0.15s',
          }}
        >
          ❄️ Clim solaire
        </button>
        <button
          type="button"
          onClick={() => setShowDimensionnementPompe(true)}
          onMouseEnter={e => e.currentTarget.style.background='#0369a1'}
          onMouseLeave={e => e.currentTarget.style.background='#0284c7'}
          style={{
            background: '#0284c7', color: 'white', border: 'none',
            borderRadius: '8px', padding: '8px 20px', fontSize: '14px',
            fontWeight: 600, cursor: 'pointer', display: 'inline-flex',
            alignItems: 'center', gap: '6px', transition: 'background 0.15s',
          }}
        >
          💧 Pompe solaire
        </button>
        <button
          type="button"
          onClick={() => setShowATP(true)}
          onMouseEnter={e => e.currentTarget.style.background='#57534e'}
          onMouseLeave={e => e.currentTarget.style.background='#78716c'}
          style={{
            background: '#78716c', color: 'white', border: 'none',
            borderRadius: '8px', padding: '8px 20px', fontSize: '14px',
            fontWeight: 600, cursor: 'pointer', display: 'inline-flex',
            alignItems: 'center', gap: '6px', transition: 'background 0.15s',
          }}
        >
          📋 ATP
        </button>
        <button
          type="button"
          onClick={() => setShowChauffeEau(true)}
          onMouseEnter={e => { e.currentTarget.style.background='#fed7aa'; e.currentTarget.style.color='#ea580c'; }}
          onMouseLeave={e => { e.currentTarget.style.background='#fff7ed'; e.currentTarget.style.color='#ea580c'; }}
          style={{
            background: '#fff7ed', color: '#ea580c', border: '1.5px solid #fdba74',
            borderRadius: '8px', padding: '8px 20px', fontSize: '14px',
            fontWeight: 600, cursor: 'pointer', display: 'inline-flex',
            alignItems: 'center', gap: '6px', transition: 'background 0.15s',
          }}
        >
          ☀️ Chauffe-eau
        </button>
        <button
          type="button"
          onClick={() => setShowFrigo(true)}
          onMouseEnter={e => { e.currentTarget.style.background='#ecfeff'; }}
          onMouseLeave={e => { e.currentTarget.style.background='white'; }}
          style={{ padding: '8px 14px', borderRadius: '8px', border: '1.5px solid #0891b2', background: 'white', color: '#0891b2', fontSize: '12px', fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
          ❄️ Réfrigération Solaire
        </button>
        <button
          type="button"
          onClick={() => setShowGenerateur(true)}
          onMouseEnter={e => { e.currentTarget.style.background='#fef9c3'; e.currentTarget.style.color='#d97706'; }}
          onMouseLeave={e => { e.currentTarget.style.background='#fffbeb'; e.currentTarget.style.color='#d97706'; }}
          style={{
            background: '#fffbeb', color: '#d97706', border: '1.5px solid #fde68a',
            borderRadius: '8px', padding: '8px 20px', fontSize: '14px',
            fontWeight: 600, cursor: 'pointer', display: 'inline-flex',
            alignItems: 'center', gap: '6px', transition: 'background 0.15s',
          }}
        >
          ⚡ Kit Générateur PV
        </button>

      </div>

      </div>
      <div id="section-options" className="mt-6 grid grid-cols-2 gap-4">
        <div>
          <label className="block mb-2 font-medium">Frais de livraison (FCFA)</label>
          <p className="text-xs text-gray-400 mb-1">0 = gratuit (Dakar/banlieue) — saisir le montant négocié avec le transporteur</p>
          <Input
            type="number"
            min="0"
            value={deliveryCost}
            onChange={e => {
              const val = e.target.value === '' ? 0 : parseFloat(e.target.value) || 0;
              setDeliveryCost(val);
              if (discountPct > 0) {
                const baseAvantRemise = items.reduce((s,i) => s + (parseFloat(i.unitPrice)||0)*(parseFloat(i.quantity)||0), 0) + val;
                setDiscount(Math.round(baseAvantRemise * discountPct / 100));
              }
            }}
            className="w-full"
          />
        </div>
        <div>
          <label className="block mb-2 font-medium">TVA</label>
          <select value={vatOption} onChange={(e) => setVatOption(e.target.value)} className="w-full border rounded p-2 focus:outline-none focus:ring-2 focus:ring-blue-300">
            <option value="without_vat">Prix Net</option>
            <option value="with_vat">Prix TTC (TVA 18% incluse)</option>
          </select>
        </div>
        <div>
          <label className="block mb-2 font-medium">Remise</label>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <div style={{ position: 'relative', width: '90px' }}>
              <input
                type="number"
                min="0"
                max="100"
                step="0.5"
                value={discountPct}
                onChange={(e) => {
                  const pct = parseFloat(e.target.value) || 0;
                  setDiscountPct(pct);
                  const baseAvantRemise = items.reduce((s,i) => s + (parseFloat(i.unitPrice)||0)*(parseFloat(i.quantity)||0), 0) + (parseFloat(deliveryCost)||0);
                  setDiscount(Math.round(baseAvantRemise * pct / 100));
                }}
                style={{ width: '100%', border: '1px solid #e5e7eb', borderRadius: '6px', padding: '6px 28px 6px 8px', fontSize: '14px', outline: 'none' }}
              />
              <span style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', fontSize: '13px', color: '#6b7280' }}>%</span>
            </div>
            <span style={{ color: '#9ca3af', fontSize: '13px' }}>=</span>
            <input
              type="number"
              min="0"
              value={discount}
              onChange={(e) => {
                const amt = parseFloat(e.target.value) || 0;
                setDiscount(amt);
                const baseAvantRemise2 = items.reduce((s,i) => s + (parseFloat(i.unitPrice)||0)*(parseFloat(i.quantity)||0), 0) + (parseFloat(deliveryCost)||0);
                setDiscountPct(baseAvantRemise2 > 0 ? Math.round(amt / baseAvantRemise2 * 1000) / 10 : 0);
              }}
              style={{ flex: 1, border: '1px solid #e5e7eb', borderRadius: '6px', padding: '6px 8px', fontSize: '14px', outline: 'none' }}
            />
            <span style={{ fontSize: '12px', color: '#6b7280', whiteSpace: 'nowrap' }}>FCFA</span>
          </div>
        </div>
      </div>

      {/* Modale Conditions */}
       {showConditions && (
         <div onClick={e => { if (e.target === e.currentTarget) setShowConditions(false); }}
           style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: '20px', backdropFilter: 'blur(6px)' }}>
           <div style={{ background: 'white', borderRadius: '16px', width: '100%', maxWidth: '600px', boxShadow: '0 25px 80px rgba(0,0,0,0.4)', overflow: 'hidden' }}>
             <div style={{ background: 'linear-gradient(135deg,#1e40af,#3b82f6)', padding: '20px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
               <div>
                 <div style={{ color: 'white', fontWeight: 700, fontSize: '18px' }}>⚙️ Conditions & Garanties</div>
                 <div style={{ color: 'rgba(255,255,255,0.8)', fontSize: '12px', marginTop: '2px' }}>Personnalisez les conditions pour ce devis</div>
               </div>
               <button onClick={() => setShowConditions(false)} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.7)', fontSize: '20px', cursor: 'pointer' }}>×</button>
             </div>
             <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
               <div>
                 <label style={{ fontSize: '13px', fontWeight: 600, color: '#374151', display: 'block', marginBottom: '6px' }}>Validité de l’offre</label>
                 <textarea value={conditionsValidite || 'Validité de l’offre : 30 jours ;  Certifications : CE ; TUV ; RoHs ; IP68, IP65.'} onChange={e => setConditionsValidite(e.target.value)} rows={2}
                   style={{ width: '100%', boxSizing: 'border-box', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '10px', fontSize: '13px', outline: 'none', resize: 'vertical', fontFamily: 'inherit', color: '#374151' }} />
               </div>
               <div>
                 <label style={{ fontSize: '13px', fontWeight: 600, color: '#374151', display: 'block', marginBottom: '6px' }}>Conditions de paiement</label>
                 <textarea value={conditionsPaiement || 'Conditions de paiement : 50% à la commande, solde à la livraison et mise en service.'} onChange={e => setConditionsPaiement(e.target.value)} rows={2}
                   style={{ width: '100%', boxSizing: 'border-box', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '10px', fontSize: '13px', outline: 'none', resize: 'vertical', fontFamily: 'inherit', color: '#374151' }} />
               </div>
               <div>
                 <label style={{ fontSize: '13px', fontWeight: 600, color: '#374151', display: 'block', marginBottom: '6px' }}>Garanties</label>
                 <textarea value={conditionsGaranties || 'Garanties : Système complet 2 ans ; Panneaux solaires 12 ans ; Rendement 86.4% à 30 ans ; Dégradation 0.40%/an.'} onChange={e => setConditionsGaranties(e.target.value)} rows={2}
                   style={{ width: '100%', boxSizing: 'border-box', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '10px', fontSize: '13px', outline: 'none', resize: 'vertical', fontFamily: 'inherit', color: '#374151' }} />
               </div>
                <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                  <button onClick={() => { setConditionsValidite(''); setConditionsPaiement(''); setConditionsGaranties(''); }}
                    style={{ padding: '9px 18px', border: '1px solid #e5e7eb', borderRadius: '8px', background: 'white', fontSize: '13px', cursor: 'pointer', color: '#6b7280' }}>
                    Réinitialiser
                  </button>
                  <button onClick={() => {
                    const cv = conditionsValidite || "Validité de l'offre : 30 jours ; Certifications : CE ; TUV ; RoHs ; IP68, IP65.";
                    const cp = conditionsPaiement || "Conditions de paiement : 50% à la commande, solde à la livraison et mise en service.";
                    const cg = conditionsGaranties || "Garanties : Système complet 2 ans ; Panneaux solaires 12 ans ; Rendement 86.4% à 30 ans ; Dégradation 0.40%/an.";
                    if (!conditionsValidite) setConditionsValidite(cv);
                    if (!conditionsPaiement) setConditionsPaiement(cp);
                    if (!conditionsGaranties) setConditionsGaranties(cg);
                    try {
                      const stored = JSON.parse(localStorage.getItem('solarquotepro_quote') || '{}');
                      stored.conditionsValidite = cv;
                      stored.conditionsPaiement = cp;
                      stored.conditionsGaranties = cg;
                      localStorage.setItem('solarquotepro_quote', JSON.stringify(stored));
                    } catch (e) { console.error('Erreur localStorage modale:', e); }
                    updateQuoteData({ conditionsValidite: cv, conditionsPaiement: cp, conditionsGaranties: cg });
                    setShowConditions(false);
                  }}
                  style={{ padding: '9px 18px', border: 'none', borderRadius: '8px', background: 'linear-gradient(135deg,#1e40af,#3b82f6)', color: 'white', fontSize: '13px', fontWeight: 700, cursor: 'pointer' }}>
                    ✓ Valider
                  </button>
                </div>
             </div>
           </div>
         </div>
       )}

      {/* Bouton Conditions */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '8px' }}>
        <button onClick={() => setShowConditions(true)}
          onMouseEnter={e => e.currentTarget.style.background = '#eff6ff'}
          onMouseLeave={e => e.currentTarget.style.background = 'white'}
          style={{ display: 'flex', alignItems: 'center', gap: '6px', border: '1px solid #bfdbfe', borderRadius: '8px', padding: '7px 14px', background: 'white', color: '#1e40af', fontSize: '13px', fontWeight: 600, cursor: 'pointer', transition: 'all 0.15s' }}>
          ⚙️ Conditions & Garanties
          {(conditionsValidite || conditionsPaiement || conditionsGaranties) && <span style={{ background: '#1e40af', color: 'white', borderRadius: '999px', padding: '1px 7px', fontSize: '10px', fontWeight: 700 }}>Modifié</span>}
        </button>
      </div>

      <div id="section-total" className="mt-6 bg-blue-50 p-4 rounded-lg">
        <h3 className="text-lg font-semibold mb-3">Récapitulatif</h3>
        <div className="space-y-1">
          <div className="flex justify-between">
            <span className="text-gray-600">Sous-total articles :</span>
            <span>{formatFCFA(items.reduce((s, i) => s + (i.quantity || 0) * (i.unitPrice || 0), 0))} FCFA</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Frais de livraison :</span>
            <span>{formatFCFA(deliveryCost)} FCFA</span>
          </div>
          {discount > 0 && (
            <div className="flex justify-between text-red-600">
              <span>Remise :</span>
              <span>- {formatFCFA(discount)} FCFA</span>
            </div>
          )}
          <div className="flex justify-between">
            <span className="text-gray-600">Sous-total HT :</span>
            <span>{formatFCFA(subtotal)} FCFA</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">TVA (18%) :</span>
            <span>{formatFCFA(vat)} FCFA</span>
          </div>
          <div className="flex justify-between text-xl font-bold border-t pt-2 mt-2">
            <span>Total TTC :</span>
            <span className="text-blue-600">{formatFCFA(total)} FCFA</span>
          </div>
          {showEuros && (
            <div className="flex justify-between text-gray-500 text-sm">
              <span>Équivalent en Euros :</span>
              <span>{(total / 655.957).toFixed(2)} €</span>
            </div>
          )}
        </div>
      </div>

      <div className="mt-4">
        <Button
          onClick={() => setShowEuros(!showEuros)}
          variant="outline"
          className="bg-gray-100 text-gray-800 hover:bg-gray-200"
        >
          {showEuros ? 'Masquer' : 'Afficher'} l'équivalent en Euros
        </Button>
      </div>

      <div className="mt-6 flex justify-between items-center">
        <div className="flex gap-2">
          <button
            type="button"
            onClick={goToPreviousStep}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
          >
            Précédent
          </button>
          <Button onClick={handleSave} variant="outline" className="border border-blue-500 text-blue-500">
            Sauvegarder
          </Button>
          <Button onClick={handleReset} variant="outline" className="border border-orange-500 text-orange-500">
            Réinitialiser
          </Button>
          <Button
            onClick={() => setShowPreview(true)}
            variant="outline"
            className="border border-purple-500 text-purple-500"
          >
            Prévisualiser
          </Button>
        </div>
        <button
          type="button"
          onClick={goToNextStep}
          className="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 font-medium"
        >
          Suivant
        </button>
      </div>

      {showPreview && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg max-w-2xl w-full max-h-[80vh] overflow-auto">
            <h3 className="text-2xl font-bold mb-4">Prévisualisation du Devis</h3>
            <div className="space-y-3">
              <div>
                <h4 className="font-bold text-gray-700">Articles ({items.length}) :</h4>
                {items.length === 0 ? (
                  <p className="text-gray-400">Aucun article</p>
                ) : (
                  <ul className="ml-4 space-y-1">
                    {items.map((item, index) => (
                      <li key={item.id} className="text-sm">
                        {index + 1}. {item.description || 'Sans description'} —
                        Qté: {item.quantity} × {formatFCFA(item.unitPrice)} FCFA =
                        <strong> {formatFCFA(item.quantity * item.unitPrice)} FCFA</strong>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div className="border-t pt-3 space-y-1 text-sm">
                <p>Frais de livraison : {formatFCFA(deliveryCost)} FCFA</p>
                <p>TVA : {vatOption === 'with_vat' ? 'Prix TTC (TVA 18% incluse)' : 'Prix Net'}</p>
                {discount > 0 && <p>Remise : {formatFCFA(discount)} FCFA</p>}
                <p>Sous-total HT : {formatFCFA(subtotal)} FCFA</p>
                <p>TVA : {formatFCFA(vat)} FCFA</p>
                <p className="font-bold text-lg">Total TTC : {formatFCFA(total)} FCFA</p>
                <p className="text-gray-500">≈ {(total / 655.957).toFixed(2)} €</p>
              </div>
            </div>
            <Button onClick={() => setShowPreview(false)} className="mt-4 bg-gray-600 text-white">
              Fermer
            </Button>
          </div>
        </div>
      )}

      {/* Modal Catalogue */}
      <AnimatePresence>
        {showCatalogue && (
          <CatalogueModal
            onClose={() => setShowCatalogue(false)}
            onAddItem={handleAddFromCatalogue}
          />
        )}

        {/* Modal Dimensionnement */}
        {showDimensionnement && (
<DimensionnementModal
          onClose={() => setShowDimensionnement(false)}
          onGenererDevis={(articles) => {
            articles.forEach(item => handleAddFromCatalogue(item));
          }}
        />
        )}
        {showGenerateur && (
          <DimensionnementGenerateurModal
            onClose={() => setShowGenerateur(false)}
            onGenererDevis={(articles) => {
              articles.forEach(item => handleAddFromCatalogue(item));
            }}
          />
        )}
        {showDimensionnementClim && (
          <DimensionnementClimModal
            onClose={() => setShowDimensionnementClim(false)}
            onGenererDevis={(articles) => {
              articles.forEach(item => handleAddFromCatalogue(item));
            }}
          />
        )}
        {showChauffeEau && (
          <DimensionnementChauffeEauModal
            onClose={() => setShowChauffeEau(false)}
            onSelect={(items) => {
              items.forEach(item => {
                if (item._titre) {
                  setItems(prev => [...prev, { id: Date.now() + Math.random(), image: item.image || '', description: item.description, quantity: 1, unitPrice: 0, _titre: true }]);
                } else {
                  setItems(prev => [...prev, { id: Date.now() + Math.random(), image: item.image || '', description: item.description, quantity: item.quantity, unitPrice: item.unitPrice }]);
                }
              });
              setShowChauffeEau(false);
            }}
          />
        )}
        {showFrigo && (
          <DimensionnementFrigoModal
            onClose={() => setShowFrigo(false)}
            onGenererDevis={(articles) => {
              articles.forEach(item => {
                if (item._titre) {
                  setItems(prev => [...prev, { id: Date.now() + Math.random(), image: '', description: item.description, quantity: 1, unitPrice: 0, _titre: true }]);
                } else {
                  setItems(prev => [...prev, { id: Date.now() + Math.random(), image: item.image || '', description: item.description, quantity: item.quantity, unitPrice: item.unitPrice }]);
                }
              });
              setShowFrigo(false);
            }}
          />
        )}

    
        {showATP && (
          <ATPModal
            onClose={() => setShowATP(false)}
            onSelect={(items) => {
              items.forEach(item => {
                if (item._titre) {
                  setItems(prev => [...prev, { id: Date.now() + Math.random(), image: item.image || '', description: item.description, quantity: 1, unitPrice: 0, _titre: true }]);
                } else {
                  setItems(prev => [...prev, { id: Date.now() + Math.random(), image: item.image || '', description: item.description, quantity: item.quantity, unitPrice: item.unitPrice }]);
                }
              });
              setShowATP(false);
            }}
          />
        )}
        
        {showDimensionnementPompe && (
          <DimensionnementPompeModal
            onClose={() => setShowDimensionnementPompe(false)}
            onSelect={(articles) => {
              articles.forEach(item => handleAddFromCatalogue(item));
            }}
          />
        )}
      </AnimatePresence>
      <QuoteFormNav />

    </QuoteFormLayout>
  );
};

export default QuoteForm;
